/**
 * orb-position.ts
 *
 * Canonical utility for Tower orb position.
 *
 * CANON §13.7 (W69 P0, 2026-06-11):
 *   The orb marks the highest floor n (1..10) where CP1, CP2, …, CPn are ALL
 *   'green'. If no CP1 is green (or no statuses at all), the orb rests at 0
 *   (base / reception level — below floor 1).
 *
 * This is the SOLE function that computes orb position. Tower.tsx and
 * PipelineTower.tsx MUST use this — no inline logic.
 *
 * Author: Bryan K. Short, Founder and CEO, Mi6 Group
 * Wave: W69 P0 Addendum (2026-06-11)
 */

/**
 * Returns the canonical orb CP position for the Tower.
 *
 * @param cpStatuses - Map<cpNumber, status|null> for a single account.
 *   Keys must be 1..10. Missing keys are treated as null (not green).
 * @returns Highest n (1..10) where CP1..CPn are ALL 'green', else 0.
 *
 * Examples:
 *   { 1:'green', 2:'green', 3:'red', ... }  → 2
 *   { 1:'orange', ... }                      → 0
 *   { 1:'green', 2:'green', 3:'green', 4:null, 5:'green' } → 3  (gap at 4)
 *   {}                                        → 0
 */
export function getOrbCp(cpStatuses: Map<number, string | null>): number {
  let highest = 0;
  for (let n = 1; n <= 10; n++) {
    const status = cpStatuses.get(n) ?? null;
    if (status === "green") {
      highest = n;
    } else {
      // Non-green (including null) breaks the contiguous run — stop here.
      break;
    }
  }
  return highest;
}

/**
 * Convenience overload: accepts a plain object or array of {cp_number, status} rows.
 *
 * @param rows - Array of {cp_number: number, status: string|null} objects
 *   (e.g. from account_cp_status query).
 * @returns Canonical orb CP position (0..10).
 */
export function getOrbCpFromRows(
  rows: Array<{ cp_number: number; status: string | null }>
): number {
  const map = new Map<number, string | null>();
  for (const row of rows) {
    map.set(row.cp_number, row.status ?? null);
  }
  return getOrbCp(map);
}
